import React from 'react';
import { FiX } from 'react-icons/fi';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  variant = 'default', // 'default', 'alert', 'confirmation', 'info'
  size = 'md', // 'sm', 'md', 'lg', 'xl'
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  contentClassName = ''
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  };

  const variantClasses = {
    default: 'bg-white',
    alert: 'bg-white border-l-4 border-red-500',
    confirmation: 'bg-white border-l-4 border-yellow-500',
    info: 'bg-white border-l-4 border-blue-500'
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${overlayClassName || ''}`}
      onClick={handleOverlayClick}
    >
      <div 
        className={`${sizeClasses[size] || ''} w-full mx-4 bg-white rounded-lg shadow-xl ${variantClasses[variant] || ''} ${className || ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {title}
          </h3>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <FiX className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className={`p-4 ${contentClassName || ''}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal Component
export const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Close",
  confirmVariant = "danger", // 'danger', 'primary', 'success'
  loading = false,
  ...props
}) => {
  const confirmButtonClasses = {
    danger: "bg-red-600 hover:bg-red-700 text-white",
    primary: "bg-blue-600 hover:bg-blue-700 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white"
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      variant="confirmation"
      {...props}
    >
      <div className="space-y-4">
        <p className="text-gray-700 text-sm">
          {message}
        </p>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 disabled:opacity-50 ${confirmButtonClasses[confirmVariant]}`}
          >
            {loading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Alert Modal Component
export const AlertModal = ({
  isOpen,
  onClose,
  title = "Alert",
  message,
  type = "info", // 'info', 'success', 'warning', 'error'
  buttonText = "OK",
  onButtonClick,
  ...props
}) => {
  const typeClasses = {
    info: "text-blue-600",
    success: "text-green-600",
    warning: "text-yellow-600",
    error: "text-red-600"
  };

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      variant="alert"
      {...props}
    >
      <div className="space-y-4">
        <p className={`text-sm ${typeClasses[type]}`}>
          {message}
        </p>
        
        <div className="flex justify-end">
          <button
            onClick={handleButtonClick}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            {buttonText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Info Modal Component
export const InfoModal = ({
  isOpen,
  onClose,
  title = "Information",
  children,
  buttonText = "Close",
  onButtonClick,
  ...props
}) => {
  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      variant="info"
      {...props}
    >
      <div className="space-y-4">
        {children}
        
        <div className="flex justify-end">
          <button
            onClick={handleButtonClick}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            {buttonText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default Modal;
